import { pb } from './pocketbase';

/**
 * Resilient PocketBase realtime subscription.
 *
 * Why this exists: the SDK's `EventSource` gives up permanently if the **first**
 * connection attempt fails (its error handler disconnects when there is no
 * `clientId` yet — i.e. before the `PB_CONNECT` event). A transient browser network
 * blip (`net::ERR_NETWORK_CHANGED`, laptop sleep/wake, Wi-Fi switch) at page load
 * therefore leaves realtime dead until a manual reload. Later drops do self-heal
 * (the SDK reconnects indefinitely once connected), so we only need to retry the
 * initial attach — and re-attach after a failed `subscribe()` promise.
 *
 * The returned function unsubscribes and cancels any pending retry.
 */
export function realtimeSubscribe(
  collection: string,
  handler: (event: unknown) => void,
  options: { retryMs?: number; onUnavailable?: () => void; onRecovered?: () => void } = {}
): () => void {
  const retryMs = options.retryMs ?? 5000;
  let active = true;
  let recovered = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let unsubscribe: (() => void) | undefined;

  const attach = (): void => {
    if (!active) return;
    pb.collection(collection)
      .subscribe('*', (event) => {
        if (active) handler(event);
      })
      .then((off) => {
        if (!active) {
          void off();
          return;
        }
        unsubscribe = off;
        if (recovered) options.onRecovered?.();
      })
      .catch(() => {
        // Initial connect failed (or a mid-session failure the SDK gave up on):
        // back off and retry, so realtime recovers without a page reload.
        if (!active) return;
        recovered = true;
        options.onUnavailable?.();
        timer = setTimeout(attach, retryMs);
      });
  };

  attach();
  return () => {
    active = false;
    if (timer) clearTimeout(timer);
    if (unsubscribe) void unsubscribe();
    else void pb.collection(collection).unsubscribe('*').catch(() => undefined);
  };
}
